import { containers, elements, feedback } from '@ecosystem/newron-design-system';
import { capitalize, isEmpty } from 'lodash';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { ComponentName, initialTrendsKpi } from '@models/Products';
import { NoResult } from '@pages/NoResult';
import { RI_PRODUCT_TREND_MARKET_CARD_TITLE } from '@pages/Utilities';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import ProductsMarketLineChart from '@data-visualization/products-market-line-chart/ProductsMarketLineChart';
import { CommonCardProps, MarketCardStateProp } from '@components/products-line-charts/Types';
import React, { useState } from 'react';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { PRODUCTS_YTD_MAT_WARNING_MSG } from '@pages/product-analyzer/Product';
import { WithAlert } from '@components/design';
import ErrorResultMessage from '@pages/ErrorResultMessage';

const TooltipContent: React.FC = () => {
  const { Tooltip } = feedback;
  return (
    <div className="flex">
      <Tooltip testId="products-market-card-tooltip" placement="top" theme="light">
        <Tooltip.Content>
          <p>
            Select a metric to see 13 period trended market performance / price information for the generated &apos;Top
            5&apos; products
          </p>
        </Tooltip.Content>
        <div className="ml-4 mt-1.5">
          <elements.Icon color="brand">
            <elements.Icon.icons.HelpOutline />
          </elements.Icon>
        </div>
      </Tooltip>
    </div>
  );
};

export interface ProductsMarketCardProps extends CommonCardProps, MarketCardStateProp {
  subSectionForAmplitudeTracking: SubSection;
  isApiError?: boolean;
}

const ProductsMarketCard: React.FC<ProductsMarketCardProps> = ({
  trendsKpi,
  selectedPeriodicity,
  selectedLineChartSaleType,
  factRevenue,
  isLoading,
  excludeTooltipIcon = false,
  isPeriodYTDorMAT,
  subSectionForAmplitudeTracking,
  isApiError = false,
}) => {
  const pageElementViewParam = useGeneratePageElementViewParam();
  const pagesConfig = usePageConfig();
  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    componentName: ComponentName.PRODUCT_TRENDS_MARKET_LINE_CHART,
    elementType: ElementType.LINE_CHART,
    elementKpi: capitalize(selectedLineChartSaleType),
    hasData: !isEmpty(trendsKpi?.items),
    access: pagesConfig.access,
    subSection: subSectionForAmplitudeTracking,
  });

  const [isDismissLineChartWarningMessage, setIsDismissLineChartWarningMessage] = useState<boolean>(false);

  const onDismissLineChartWarningMessage = () => {
    setIsDismissLineChartWarningMessage(true);
  };

  const { ref: ProductMarketLineChartComponent } = useInViewTracking(customPageElementViewParam);

  const renderContent = () => {
    return renderVisualization({
      isLoading: isLoading,
      fetchedApiData: trendsKpi?.items,
      defaultData: initialTrendsKpi.items,
      jsxToRender: (
        <div ref={ProductMarketLineChartComponent}>
          {Boolean(trendsKpi?.items?.length) && (
            <ProductsMarketLineChart
              data={trendsKpi}
              selectedPeriodicity={selectedPeriodicity}
              currency={factRevenue.currencySymbol}
              selectedLineChartSaleType={selectedLineChartSaleType}
            />
          )}
        </div>
      ),
      noResultJsx: <div ref={ProductMarketLineChartComponent}>{NoResult()}</div>,
      errorMessageJsx: <div ref={ProductMarketLineChartComponent}>{ErrorResultMessage()}</div>,
      isError: isApiError,
    });
  };

  const { Card } = containers;
  return (
    <Card>
      <Card.Title title={RI_PRODUCT_TREND_MARKET_CARD_TITLE}>
        <TooltipContentHelper excludeTooltipIcon={excludeTooltipIcon} />
      </Card.Title>
      {!isDismissLineChartWarningMessage && isPeriodYTDorMAT && (
        <div className="my-4" data-testid="periodicity-alert-warning-message">
          <WithAlert title={PRODUCTS_YTD_MAT_WARNING_MSG} onClick={onDismissLineChartWarningMessage} />
        </div>
      )}
      {renderContent()}
    </Card>
  );
};

const TooltipContentHelper = ({ excludeTooltipIcon }: { excludeTooltipIcon: boolean }) => {
  if (excludeTooltipIcon) {
    return null;
  }

  return (
    <div style={{ flex: '1' }}>
      <TooltipContent />
    </div>
  );
};

export default ProductsMarketCard;
